import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Clapperboard, Loader2, Pause, Play } from "lucide-react";
import { http, errMsg } from "../lib/api";

const dot = { done: "bg-emerald-500", failed: "bg-red-500", writing: "bg-[#C9A847] animate-pulse", waiting: "bg-stone-300" };

const ScopeRow = ({ s, onGo, busy }) => {
  const left = s.total - s.have;
  return (
    <li data-testid={`vb-scope-${s.scope}`} className="flex items-center gap-4 border-t border-stone-100 py-3">
      <span className="w-28 text-[11px] uppercase tracking-[0.18em] text-stone-500">{s.kind}</span>
      <b className="flex-1 text-[#2B2A26]">{s.label}</b>
      <span data-testid={`vb-scope-count-${s.scope}`} className="text-sm text-stone-600 font-mono-ui">{s.have}/{s.total} done</span>
      <button data-testid={`vb-start-${s.scope}`} disabled={busy || !left} onClick={() => onGo(s)}
        className="inline-flex items-center gap-2 rounded-sm bg-[#2B2A26] px-3 py-1.5 text-xs font-bold text-white hover:bg-black transition-colors disabled:opacity-30">
        <Clapperboard size={13} /> {left ? `Create ${left} missing` : "All done"}
      </button>
    </li>
  );
};

const Run = ({ r, onStop, onResume }) => {
  const pct = r.total ? Math.round(((r.done + r.failed) / r.total) * 100) : 0;
  const running = r.state === "running";
  return (
    <section data-testid="vb-run" className="rounded-lg border border-stone-200 bg-white p-5 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {running && <Loader2 size={16} className="animate-spin text-[#C9A847]" />}
        <b className="text-[#2B2A26]">{r.label}</b>
        <span data-testid="vb-run-state" className="text-xs uppercase tracking-wider text-stone-500">{r.state}</span>
        <span data-testid="vb-run-counts" className="text-sm text-stone-600">{r.done} done · {r.failed} failed · {r.total} to write{r.skipped ? ` · ${r.skipped} already had prompts (skipped)` : ""}</span>
        {running && <button data-testid="vb-stop" onClick={onStop} className="ml-auto inline-flex items-center gap-1 rounded-sm border border-stone-300 px-3 py-1.5 text-xs hover:bg-stone-100 transition-colors"><Pause size={13} /> Stop</button>}
        {["paused", "stopped"].includes(r.state) && <button data-testid="vb-resume" onClick={onResume} className="ml-auto inline-flex items-center gap-1 rounded-sm bg-[#C9A847] px-3 py-1.5 text-xs font-bold text-white hover:opacity-90 transition-opacity"><Play size={13} /> Resume</button>}
      </div>
      <div className="h-2 rounded-full bg-stone-100 overflow-hidden"><div data-testid="vb-progress" className="h-full bg-[#C9A847] transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      {r.error && <p data-testid="vb-error" className="text-sm text-red-700">{r.error}</p>}
      <ul className="grid md:grid-cols-2 gap-x-6 gap-y-1 max-h-80 overflow-y-auto text-sm">
        {r.queue.map((i) => (
          <li key={i.id} data-testid={`vb-item-${i.id}`} className="flex items-center gap-2 py-0.5">
            <span className={`h-2 w-2 shrink-0 rounded-full ${dot[i.state]}`} />
            <Link to={i.coll === "kits" ? `/kits/${i.id}` : `/extras/products/${i.id}`} className="truncate hover:underline">{i.name}</Link>
            {i.error && <span className="truncate text-xs text-red-600" title={i.error}>{i.error}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function VideoBulk() {
  const [scopes, setScopes] = useState(null);
  const [run, setRun] = useState(null);
  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([http.get("/video-prompts/scopes"), http.get("/video-prompts/bulk")]);
      setScopes(s.data.scopes); setRun(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const running = run?.state === "running";
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [running, load]);
  const act = (fn, msg) => async (...a) => { try { const r = await fn(...a); if (msg) toast.message(msg(r)); load(); } catch (e) { toast.error(errMsg(e)); } };
  const go = act((s) => http.post("/video-prompts/bulk", { scope: s.scope }), (r) => `Writing video prompts for ${r.data.total} products — keeps going in the background`);
  const stop = act(() => http.post("/video-prompts/bulk/stop"), () => "Stopping after the current products finish");
  const resume = act(() => http.post("/video-prompts/bulk/resume"), () => "Resumed");
  return (
    <div data-testid="video-bulk-page" className="p-10 max-w-5xl space-y-8">
      <header>
        <div className="eyebrow">Marketing</div>
        <h1 className="font-display text-4xl text-[#2B2A26] mt-2">Bulk video prompts</h1>
        <p className="text-stone-600 mt-2 text-sm max-w-2xl">One click writes Higgsfield prompt packs (20 s, 40 s, 1 min, vertical and YouTube) with Instagram, YouTube, Facebook and TikTok captions for every product in an edition. Products that already have prompts are skipped.</p>
      </header>
      {run && run.state !== "idle" && <Run r={run} onStop={stop} onResume={resume} />}
      <section className="rounded-lg border border-stone-200 bg-white p-5">
        <b className="text-[#2B2A26]">Pick an edition</b>
        {!scopes ? <Loader2 size={16} className="animate-spin mt-3" /> : (
          <ul data-testid="vb-scopes" className="mt-2">{scopes.map((s) => <ScopeRow key={s.scope} s={s} onGo={go} busy={running} />)}</ul>
        )}
      </section>
    </div>
  );
}
